import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { PageDataType } from '@common/paged-data/enums/page-data-type.enum';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { CentralPermissions } from '@portal-core/permissions/enums/central-permissions.enum';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { SiteTeamsListComponent } from '@portal-core/sites/components/site-teams-list/site-teams-list.component';
import { Site } from '@portal-core/sites/models/site.model';
import { SitesService } from '@portal-core/sites/services/sites.service';
import { TeamsService } from '@portal-core/teams/services/teams.service';
import { AutocompleteInputComponent } from '@portal-core/ui/autocomplete/components/autocomplete-input/autocomplete-input.component';
import { PageFilterService } from '@portal-core/ui/page-filters/services/page-filter.service';
import { PopupComponent } from '@portal-core/ui/popup/components/popup/popup.component';
import { PopupTriggerDirective } from '@portal-core/ui/popup/directives/popup-trigger/popup-trigger.directive';
import { LoadingState } from '@portal-core/util/loading-state';
import { Observable, Subscription } from 'rxjs';

export enum SiteAccessFormTab {
  Teams = 0
}

@Component({
  selector: 'mc-site-access-form',
  templateUrl: './site-access-form.component.html',
  styleUrls: ['./site-access-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteAccessFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() site: Site;
  @Input() selectedTabIndex?: SiteAccessFormTab = 0;

  @Output() saved: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(SiteTeamsListComponent, { static: false }) siteTeamsListComponent?: SiteTeamsListComponent;
  @ViewChild('siteTeamsAutoComplete', { static: false }) siteTeamsAutocomplete: AutocompleteInputComponent;
  @ViewChild('popupTriggerTeams', { static: false, read: PopupTriggerDirective }) popupTriggerTeams: PopupTriggerDirective;
  @ViewChild('addSiteTeamsPopup', { static: false }) addSiteTeamsPopup: PopupComponent;
  @ViewChild('filterSiteTeams', { static: false }) filterSiteTeams: ElementRef;
  siteTeamsFilterName: string = 'site-teams';
  filterSiteTeamsSubscription: Subscription;

  savingState: LoadingState<string> = new LoadingState<string>();

  canManageSites$: Observable<boolean>;

  constructor(
    private errorService: ErrorService,
    private permissionsService: PermissionsService,
    private sitesService: SitesService,
    private teamsService: TeamsService,
    private pageFilterService: PageFilterService
  ) { }

  ngOnInit() {
    // create an observable on whether editing is allowed
    this.canManageSites$ = this.permissionsService.currentUserHasPermission$(CentralPermissions.ManageSites);
  }

  ngAfterViewInit(): void {
    this.initFilterSubscriptions();

    // set default team order
    this.teamsService.setPagedDataListOrder$(this.siteTeamsListComponent.dataListId, 'Name', 'asc');
    this.teamsService.setPagedDataListOrder$(this.siteTeamsAutocomplete.selectList.dataListId, 'Name', 'asc');
  }

  ngOnDestroy(): void {
    this.filterSiteTeamsSubscription?.unsubscribe();
    this.filterSiteTeamsSubscription = null;
  }

  onSaved() {
    this.saved.emit();
  }

  initFilterSubscriptions() {
    if (!this.filterSiteTeamsSubscription) {
      this.filterSiteTeamsSubscription = this.siteTeamsListComponent?.getFilter$(this.siteTeamsFilterName).subscribe(filter => {
        if (!filter) {
          this.filterSiteTeams.nativeElement.value = '';
        }
      });
    }
  }

  onAddSiteTeamsClicked() {
    if (this.siteTeamsAutocomplete.value) {
      this.savingState.update(true);

      this.sitesService.addSiteTeams$(this.site.Id, this.siteTeamsAutocomplete.value as number[]).subscribe(() => {
        this.siteTeamsListComponent.hardReload();
        this.siteTeamsAutocomplete.close();
        this.popupTriggerTeams.close();
        this.savingState.update(false);
        this.saved.emit();
      }, error => {
        this.savingState.update(false, 'There was an error adding teams to the site.', this.errorService.getErrorMessages(error));
      });
    }
  }

  onCancelSiteTeamsClicked() {
    this.savingState.update(false);
    this.addSiteTeamsPopup.close();
  }

  onSiteTeamsPopupClosed() {
    this.siteTeamsAutocomplete.value = null;
  }

  onFilterSiteTeamsKeyUp(event: any) {
    const fb = this.pageFilterService.create({
      Id: this.siteTeamsFilterName,
      Type: PageFilterGroupType.Search
    });
    fb.search(['Name'], event.target.value, [
      { PropertyName: 'Name', PropertyType: PageDataType.String }
    ]);
    this.teamsService.setPagedDataListFilterByName$(this.siteTeamsListComponent.dataListId, this.siteTeamsFilterName, fb.value);
  }
}
